import React from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useIntl } from 'react-intl';
import { Alert, Table } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { EMPTY, PRETTY_DATE_TIME_FORMAT } from '@owl-frontend/components';
import { type Disruption, SeverityLevel } from '../../api/status/interface';
import OwlLogo from '../../assets/logo-28.svg';
import { Locale } from '../../context/AppProvider';
import useStatusHook from './useStatusHook';
import styles from './StatusContainer.module.scss';

// This is for dayjs to initialize for timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const severityRank: Record<SeverityLevel, number> = {
  [SeverityLevel.FULL]: 4,
  [SeverityLevel.PARTIAL]: 3,
  [SeverityLevel.SPOT]: 2,
  [SeverityLevel.NOTE]: 1,
};

const formatToLocalTime = (utcTime: string): string => {
  if (!utcTime) {
    return EMPTY;
  }
  const localTime = dayjs.utc(utcTime).local();
  const timezoneAbbr = localTime.format('z');
  return `${localTime.format(PRETTY_DATE_TIME_FORMAT)} (${timezoneAbbr})`;
};

const DisruptionAlert: React.FC<{ disruptions: Disruption[] }> = ({
  disruptions,
}) => {
  const intl = useIntl();

  const allDisruptions = React.useMemo(() => {
    const flattenedDisruptions: Array<Omit<Disruption, 'grouped'>> =
      disruptions.reduce((acc, curr) => {
        const { grouped, ...rest } = curr;

        return [...acc, rest, ...grouped];
      }, [] as Array<Omit<Disruption, 'grouped'>>);

    return flattenedDisruptions;
  }, [disruptions]);

  const allResolved = allDisruptions.every(
    (disruption) => disruption.resolution_time !== null
  );

  if (disruptions.length === 0 || allResolved) {
    return (
      <Alert
        message={intl.formatMessage({ id: 'status.severity.note' })}
        type="success"
      />
    );
  }

  const unresolved = allDisruptions.filter((d) => !d.resolution_time);

  const highestUnresolved = unresolved.reduce((max, d) => {
    return severityRank[d.severity] > severityRank[max.severity] ? d : max;
  }, unresolved[0]);

  let alertType: 'error' | 'warning' | 'info' = 'info';
  let message = '';

  switch (highestUnresolved.severity) {
    case 'FULL':
      alertType = 'error';
      message = intl.formatMessage(
        { id: 'status.severity.full' },
        { disruption_text: highestUnresolved.disruption }
      );
      break;
    case 'PARTIAL':
      alertType = 'warning';
      message = intl.formatMessage(
        { id: 'status.severity.partial' },
        { disruption_text: highestUnresolved.disruption }
      );
      break;
    case 'SPOT':
      alertType = 'warning';
      message = intl.formatMessage(
        { id: 'status.severity.spot' },
        { disruption_text: highestUnresolved.disruption }
      );
      break;
  }
  return <Alert message={message} type={alertType} />;
};

const StatusContainer = () => {
  const { messages } = React.useContext(Locale);
  const [{ disruptions }, handler] = useStatusHook();
  const entryTimeColRef = React.useRef<HTMLTableCellElement>(null);
  const [expandOffset, setExpandOffset] = React.useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (disruptions.length > 0) {
      const keysToExpand = disruptions
        .filter((disruption) => {
          if (
            !disruption.resolution_time &&
            disruption.severity !== SeverityLevel.NOTE
          ) {
            return true;
          }

          return disruption.grouped.some(
            (child) =>
              !child.resolution_time && child.severity !== SeverityLevel.NOTE
          );
        })
        .map((disruption) => disruption.disruption_id);

      setExpandedRowKeys(keysToExpand);
    }
  }, [disruptions]);

  const handleExpand = (expanded: boolean, record: Disruption) => {
    if (expanded) {
      setExpandedRowKeys((prev) => [...prev, record.disruption_id]);
    } else {
      setExpandedRowKeys((prev) =>
        prev.filter((key) => key !== record.disruption_id)
      );
    }
  };

  const columns: ColumnType<Disruption>[] = React.useMemo(
    () => [
      {
        title: () => (
          <div ref={entryTimeColRef}>{messages['status.incident_time']}</div>
        ),
        dataIndex: 'entry_time',
        key: 'entry_time',
        width: 100,
        className: styles.entryTimeCell,
        render: (value: string, disruption: Disruption) => {
          return (
            <div className={styles.entryTimeContainer}>
              <div
                className={clsx(
                  styles.disruptionBar,
                  styles[`severity-${disruption.severity.toLowerCase()}`]
                )}
              />
              {formatToLocalTime(value)}
            </div>
          );
        },
      },
      {
        title: messages['status.status'],
        dataIndex: 'disruption',
        key: 'disruption',
        minWidth: 200,
        width: 300,
        render: (value: string, disruption: Disruption) => {
          return (
            <div>
              {value} <br />
              <div style={{ color: '#8f8b8b' }}>
                {disruption.disruption_detail}{' '}
              </div>
            </div>
          );
        },
      },
      Table.EXPAND_COLUMN,
    ],
    [messages]
  );

  React.useEffect(() => {
    handler.fetchDisruptions();
  }, [handler]);

  React.useEffect(() => {
    const measureOffset = () => {
      if (entryTimeColRef.current) {
        const width = entryTimeColRef.current.offsetWidth;
        setExpandOffset(width);
      }
    };

    measureOffset();
    window.addEventListener('resize', measureOffset);
    return () => window.removeEventListener('resize', measureOffset);
  }, []);

  return (
    <div className={styles.container}>
      <OwlLogo className={styles.logo} />
      <DisruptionAlert disruptions={disruptions} />
      <Table
        rowKey="disruption_id"
        dataSource={disruptions}
        columns={columns}
        pagination={false}
        expandedRowKeys={expandedRowKeys}
        onExpand={handleExpand}
        expandable={{
          expandedRowRender: (record) => (
            <div className={styles.expandedRowContainer}>
              {record.grouped.length > 0 &&
                record.grouped.map((item: Omit<Disruption, 'grouped'>) => (
                  <div
                    key={item.disruption_id}
                    className={styles.followUpContainer}
                  >
                    <div
                      style={{
                        width: expandOffset + 32,
                      }}
                      className={styles.dateContainer}
                    >
                      <div
                        className={clsx(
                          styles.disruptionBar,
                          styles.expandedDisruptionBar,
                          styles[`severity-${item.severity.toLowerCase()}`]
                        )}
                      />
                      {formatToLocalTime(item.entry_time)}
                    </div>
                    <div style={{ left: expandOffset + 32, flex: 1 }}>
                      {item.disruption_detail}
                    </div>
                  </div>
                ))}
            </div>
          ),
          rowExpandable: (record) => record.grouped.length > 0,
          expandIcon: ({ expanded, onExpand, record }) =>
            record.grouped.length > 0 ? (
              expanded ? (
                <UpOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <DownOutlined onClick={(e) => onExpand(record, e)} />
              )
            ) : null,
        }}
      />
    </div>
  );
};

export default StatusContainer;
